// Small wrapper for the admin token storage.
//
// Why sessionStorage instead of localStorage?
// ------------------------------------------------------------
// XSS attack surface: localStorage persists across browser sessions and is
// readable by ANY JS executing on the page. If an attacker manages to inject
// a script (XSS), they can exfiltrate the token at any later time, even days
// after the admin last visited. sessionStorage drops the value when the
// browser tab closes, dramatically shrinking the attack window.
//
// Trade-off: the admin must sign in again after closing the tab. Acceptable
// for a low-volume internal dashboard.
//
// SECURITY (Feb 2026 incident): the storage key is now namespaced with a
// version suffix. Bumping `xa.token.version` invalidates ALL previously-
// cached tokens on every browser without the user needing to clear cache —
// they will see the login screen on next page load.

const STORAGE_VERSION = "v3";
const KEY = `xa-admin-token-${STORAGE_VERSION}`;
const EPOCH_KEY = `xa-admin-epoch-${STORAGE_VERSION}`;
// Legacy keys whose contents we now intentionally drop on first read so
// any tokens stolen pre-incident are useless even on a returning browser.
const LEGACY_KEYS = ["xa-admin-token", "xa-admin-token-v2", "xa-admin-epoch-v2"];

function purgeLegacy() {
  try {
    for (const k of LEGACY_KEYS) {
      window.sessionStorage.removeItem(k);
      window.localStorage.removeItem(k);
    }
  } catch {
    /* storage blocked */
  }
}

export const tokenStorage = {
  get() {
    try {
      purgeLegacy();
      return window.sessionStorage.getItem(KEY) || "";
    } catch {
      return "";
    }
  },

  set(value) {
    try {
      purgeLegacy();
      window.sessionStorage.setItem(KEY, value);
    } catch {
      /* storage blocked — nothing else we can do */
    }
  },

  clear() {
    try {
      purgeLegacy();
      window.sessionStorage.removeItem(KEY);
      window.sessionStorage.removeItem(EPOCH_KEY);
    } catch {
      /* ignore */
    }
  },

  // ── Session-epoch (Feb 2026) ────────────────────────────────────
  // Bumped server-side when the admin clicks "Revoke all sessions".
  // Sent on every admin request as X-Session-Epoch so a stale browser
  // token cannot survive a revocation.
  getEpoch() {
    try {
      return window.sessionStorage.getItem(EPOCH_KEY) || "";
    } catch {
      return "";
    }
  },
  setEpoch(v) {
    try {
      window.sessionStorage.setItem(EPOCH_KEY, String(v));
    } catch {
      /* ignore */
    }
  },
};
